import { FC, useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";
import { formatISO, parseISO } from "date-fns";
import { View, ViewDensity } from "@common/types/views/views";
import { Entity } from "@common/types/entity";
import { useLedgersByEntityIds } from "@api/ledger.api";
import {
    saveTaxPackage,
    selectTaxPackageLedgerId,
} from "@components/accounting/useTaxPackageDownload";
import { ReportPackageBuilderDrawer } from "@components/accounting/ReportPackageBuilderDrawer/ReportPackageBuilderDrawer";
import { ReportPackageModal } from "@components/accounting/ReportPackageModal/ReportPackageModal";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useFileDownload } from "@lib/fileDownload";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { CollapseAllButton } from "@design-system/ViewTable/CollapseAllButton";
import { GroupBySelect } from "@design-system/ViewTable/grouping/GroupBySelect";
import { GroupByRow } from "@design-system/ViewTable/grouping/groupByRowTypes";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import interfaceStyles from "@components/mainInterface/MainInterface.module.scss";
import { useConsolidationReportContext } from "@components/accounting/consolidationReports/useConsolidationReportContext";
import {
    getDefaultEndDate,
    getDefaultStartDate,
} from "@components/accounting/useProfitLossInitialDates";
import {
    buildReportingCatalog,
    REPORTING_AR_AGING_REPORTS,
    REPORTING_CHECK_DETAIL_REPORTS,
    REPORTING_FORM_1099_NEC_REPORTS,
    REPORTING_ENTITY_REPORTS,
    REPORTING_SCHEDULES_REPORTS,
    ReportingCatalogItem,
} from "./reportingCatalog";
import { ReportingEntityFilter } from "./ReportingEntityFilter";
import { ReportingGroupByColumn } from "./reportingGroupByConfig";
import {
    REPORTING_GROUP_BY_OPTIONS,
    ReportingGroupBy,
    ReportingSort,
} from "./reportingControls";
import { useReportingOrganization } from "./useReportingOrganization";
import { useReportingEntityScope } from "./useReportingEntityScope";
import styles from "./ReportingPage.module.scss";

const REPORTING_VIEW_OPTIONS = { density: ViewDensity.MD };

const REPORTING_ENTITY_VIEW: View = {
    columns: [{ id: "report" }, { id: "description" }],
    filters: {},
    options: REPORTING_VIEW_OPTIONS,
};

const REPORTING_REPORT_VIEW: View = {
    columns: [{ id: "scope" }, { id: "description" }],
    filters: {},
    options: REPORTING_VIEW_OPTIONS,
};

export const ReportingPage: FC = () => {
    const navigate = useStateAwareNavigate();
    const {
        entities,
        isScopeRequested,
        selectedEntity,
        selectedConsolidationGroupId,
        setSelectedEntityId,
    } = useReportingEntityScope();
    const consolidationResult = useConsolidationReportContext();
    const consolidationContext =
        consolidationResult.status === "ready"
            ? consolidationResult.context
            : undefined;

    const {
        schedules: canUseSchedules,
        form1099NecReport: canUseForm1099NecReport,
        expandedReportPackage: isExpandedReportPackageEnabled,
    } = useTypedFlags();
    const ReportPackageDialog = isExpandedReportPackageEnabled
        ? ReportPackageBuilderDrawer
        : ReportPackageModal;
    const [startDate, setStartDate] = useState<Date>(getDefaultStartDate);
    const [endDate, setEndDate] = useState<Date>(getDefaultEndDate);
    const [search, setSearch] = useState("");
    const [groupBy, setGroupBy] = useState<ReportingGroupBy>("entity");
    const [sort, setSort] = useState<ReportingSort>("default");
    const [reportPackageEntityId, setReportPackageEntityId] = useState<
        number | undefined
    >(undefined);
    const tableRef = useRef<ViewTableHandle>(null);
    const { download: downloadTaxPackage } = useFileDownload(
        async (entity: Entity, ledgerId: string) => {
            await saveTaxPackage({ entity, ledgerId });
        },
    );

    const entityById = useMemo(
        () => new Map(entities.map((entity) => [entity.id, entity])),
        [entities],
    );
    const entityIds = useMemo(
        () => entities.map((entity) => entity.id),
        [entities],
    );
    const { data: ledgersByEntityId } = useLedgersByEntityIds(entityIds);
    const entityReports = useMemo(
        () => [
            ...REPORTING_ENTITY_REPORTS,
            ...REPORTING_AR_AGING_REPORTS,
            ...(canUseSchedules ? REPORTING_SCHEDULES_REPORTS : []),
            ...REPORTING_CHECK_DETAIL_REPORTS,
            ...(canUseForm1099NecReport ? REPORTING_FORM_1099_NEC_REPORTS : []),
        ],
        [canUseSchedules, canUseForm1099NecReport],
    );
    const reportPackageDateRange = useMemo(
        () => ({
            start: formatISO(startDate, { representation: "date" }),
            end: formatISO(endDate, { representation: "date" }),
        }),
        [endDate, startDate],
    );

    const catalog = useMemo(
        () =>
            buildReportingCatalog({
                entities,
                consolidationContext,
                startDate,
                endDate,
                entityReports,
            }),
        [entities, consolidationContext, startDate, endDate, entityReports],
    );
    const scopedCatalog = useMemo(() => {
        if (!isScopeRequested) {
            return catalog;
        }
        if (selectedEntity) {
            return catalog.filter(
                (item) =>
                    item.scope.kind === "entity" &&
                    item.scope.id === selectedEntity.id,
            );
        }
        if (selectedConsolidationGroupId) {
            return catalog.filter(
                (item) =>
                    item.scope.kind === "consolidationGroup" &&
                    item.scope.id === selectedConsolidationGroupId,
            );
        }
        return [];
    }, [
        catalog,
        isScopeRequested,
        selectedConsolidationGroupId,
        selectedEntity,
    ]);

    const openReport = useCallback(
        (item: ReportingCatalogItem) => {
            switch (item.kind) {
                case "report":
                    navigate(item.url);
                    return;
                case "taxPackage": {
                    if (item.scope.kind !== "entity") {
                        return;
                    }
                    const entity = entityById.get(item.scope.id);
                    const ledgerId = selectTaxPackageLedgerId(
                        ledgersByEntityId?.[item.scope.id],
                    );
                    if (!entity || !ledgerId) {
                        return;
                    }
                    void downloadTaxPackage(entity, ledgerId);
                    return;
                }
                case "reportPackage":
                    if (item.scope.kind !== "entity") {
                        return;
                    }
                    setReportPackageEntityId(item.scope.id);
                    return;
                default: {
                    const unreachable: never = item;
                    return unreachable;
                }
            }
        },
        [downloadTaxPackage, entityById, ledgersByEntityId, navigate],
    );

    const { expanded, groupedViewProps } = useReportingOrganization({
        catalog: scopedCatalog,
        entityById,
        groupBy,
        sort,
        search,
        parentName: consolidationContext?.parentEntity.name ?? "Group",
    });

    return (
        <article
            className={classNames(
                interfaceStyles.fullHeightContent,
                styles.page,
            )}
        >
            <TablePageHeader
                isFullPage
                search={search}
                onSearchChange={setSearch}
                filterContainerClassName={styles.toolbar}
                prependChildren={
                    <DateRangeFilterNext
                        dateRange={toDateOnlyDateRange({
                            start: startDate,
                            end: endDate,
                        })}
                        requireSelection
                        onChange={(value: DateOnlyDateRange) => {
                            if (value.start) {
                                setStartDate(parseISO(value.start));
                            }
                            if (value.end) {
                                setEndDate(parseISO(value.end));
                            }
                        }}
                    />
                }
            >
                <CollapseAllButton expanded={expanded} tableRef={tableRef} />
                <GroupBySelect
                    value={groupBy}
                    options={REPORTING_GROUP_BY_OPTIONS}
                    onValueChange={(value) => {
                        if (value) {
                            setGroupBy(value);
                        }
                    }}
                />
                {selectedEntity && (
                    <ReportingEntityFilter
                        entity={selectedEntity}
                        onChange={setSelectedEntityId}
                    />
                )}
            </TablePageHeader>

            <ViewTable<
                GroupByRow<ReportingCatalogItem>,
                ReportingGroupByColumn,
                ReportingSort
            >
                {...groupedViewProps}
                ref={tableRef}
                view={
                    groupBy === "report"
                        ? REPORTING_REPORT_VIEW
                        : REPORTING_ENTITY_VIEW
                }
                sort={sort}
                onSortChange={setSort}
                onSortRemove={() => setSort("default")}
                fixedTopOffsetPx
                enableKeyboardShortcuts
                onRowClick={(row) => {
                    if (row?.rowType === "item") {
                        openReport(row.item);
                    }
                }}
                getRowClassName={(row) =>
                    row.rowType === "item"
                        ? viewTableStyles.clickableRow
                        : undefined
                }
                emptyView={renderEmptyView}
            />
            <ReportPackageDialog
                show={reportPackageEntityId !== undefined}
                onHide={() => setReportPackageEntityId(undefined)}
                defaultEntityId={reportPackageEntityId}
                defaultDateRange={reportPackageDateRange}
            />
        </article>
    );
};

const renderEmptyView = () => (
    <EmptyState header="No reports found" body="Try clearing the search." />
);
